import React from 'react';
import Logo from './Logo';

const DEFAULT_TAGLINE =
  'Automated carrier sourcing for shippers. Tender every load, compare bids to live market rates, and award the best carrier — without manual phone tag.';

const DEFAULT_COMPANY_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Customers', href: '#customers' },
  { label: 'Careers', href: '#careers' },
  { label: 'Contact', href: 'mailto:hello@vibeengine.io' },
];

const DEFAULT_RESOURCE_LINKS = [
  { label: 'Docs', href: '#docs' },
  { label: 'API reference', href: '#api' },
  { label: 'Security', href: '#security' },
  { label: 'Status', href: '#status' },
];

const DEFAULT_LEGAL_LINKS = [
  { label: 'Privacy', href: '#privacy' },
  { label: 'Terms', href: '#terms' },
  { label: 'Security', href: '#security' },
];

/**
 * Shared site footer.
 *
 * Per-page customisation:
 *   - `productLinks`     in-page anchor links for the current landing page
 *                        (typically mirrors the navbar links)
 *   - `tagline`          short blurb under the logo
 *   - `companyLinks`     overrides the default Company column
 *   - `resourceLinks`    overrides the default Resources column
 *   - `legalLinks`       overrides the default Privacy / Terms row
 *   - `productLabel`     heading for the productLinks column (defaults to "Product")
 */
export default function Footer({
  productLinks = [],
  productLabel = 'Product',
  tagline = DEFAULT_TAGLINE,
  companyLinks = DEFAULT_COMPANY_LINKS,
  resourceLinks = DEFAULT_RESOURCE_LINKS,
  legalLinks = DEFAULT_LEGAL_LINKS,
}) {
  const groups = [
    { title: productLabel, items: productLinks },
    { title: 'Company', items: companyLinks },
    { title: 'Resources', items: resourceLinks },
  ].filter((g) => g.items && g.items.length > 0);

  return (
    <footer className="bg-white border-t border-ink-200">
      <div className="container-page py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2">
            <Logo />
            <p className="mt-4 text-sm text-ink-600 max-w-sm">{tagline}</p>
          </div>

          {groups.map((g) => (
            <div key={g.title}>
              <div className="text-xs uppercase tracking-wider font-semibold text-ink-500">
                {g.title}
              </div>
              <ul className="mt-4 space-y-2">
                {g.items.map((i) => (
                  <li key={i.label}>
                    <a
                      href={i.href}
                      className="text-sm text-ink-700 hover:text-ink-900"
                    >
                      {i.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-ink-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-ink-500">
            © {new Date().getFullYear()} Vibe Engine. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-xs text-ink-500">
            {legalLinks.map((l) => (
              <a key={l.label} href={l.href} className="hover:text-ink-900">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
